import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Sparkles, Heart } from 'lucide-react';
import confetti from 'canvas-confetti';

interface SurpriseProps {
    onReveal?: () => void;
}

const Surprise: React.FC<SurpriseProps> = ({ onReveal }) => {
    const [stage, setStage] = useState<'hidden' | 'revealing' | 'revealed'>('hidden');
    const { t } = useLanguage();

    const handleReveal = () => {
        setStage('revealing');

        const duration = 5 * 1000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

        function randomInRange(min: number, max: number) {
            return Math.random() * (max - min) + min;
        }

        const interval: any = setInterval(function () {
            const timeLeft = animationEnd - Date.now();
            if (timeLeft <= 0) return clearInterval(interval);

            const particleCount = 50 * (timeLeft / duration);
            confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } }));
            confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } }));
        }, 250);

        setTimeout(() => {
            setStage('revealed');
            if (onReveal) onReveal();
        }, 2000);
    };

    return (
        <div className="px-6 py-8 flex flex-col items-center justify-center min-h-[400px]">
            <AnimatePresence mode="wait">
                {stage === 'hidden' && (
                    <motion.div
                        key="hidden"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.2 }}
                        className="flex flex-col items-center text-center"
                    >
                        <div className="relative mb-6">
                            <motion.div
                                animate={{ rotate: [0, -10, 10, -10, 0], scale: [1, 1.1, 1] }}
                                transition={{ repeat: Infinity, duration: 2 }}
                                onClick={handleReveal}
                                className="w-32 h-32 md:w-40 md:h-40 bg-primary-red rounded-[2rem] shadow-2xl flex items-center justify-center cursor-pointer border-4 border-white dark:border-gray-800"
                            >
                                <Gift className="text-white" size={48} />
                            </motion.div>
                        </div>
                        <h2 className="text-2xl font-playfair font-bold text-primary-red mb-2">You have a surprise!</h2>
                        <p className="text-gray-500 dark:text-gray-400 font-poppins text-sm">Tap the gift box to open your special 50-day surprise.</p>
                    </motion.div>
                )}

                {stage === 'revealing' && (
                    <motion.div key="revealing" className="flex flex-col items-center justify-center">
                        <motion.div
                            animate={{ scale: [1, 5, 20], rotate: [0, 90, 180, 360], opacity: [1, 1, 0] }}
                            transition={{ duration: 2, ease: "easeInOut" }}
                            className="text-primary-red"
                        >
                            <Sparkles size={60} />
                        </motion.div>
                    </motion.div>
                )}

                {stage === 'revealed' && (
                    <motion.div
                        key="revealed"
                        initial={{ opacity: 0, scale: 0.9, y: 30 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        className="w-full max-w-4xl bg-white dark:bg-romantic-cardDark p-6 md:p-10 rounded-[2.5rem] shadow-2xl border border-primary-red/5 relative overflow-hidden flex flex-col items-center text-center"
                    >
                        <div className="w-14 h-14 bg-primary-red/10 rounded-full flex items-center justify-center mb-4">
                            <Heart className="text-primary-red fill-current" size={28} />
                        </div>
                        <h2 className="text-2xl md:text-3xl font-playfair font-bold text-primary-red mb-4 leading-tight">
                            {t('surprise.revealedTitle')}
                        </h2>
                        <div className="bg-primary-red/5 dark:bg-primary-red/10 p-5 md:p-8 rounded-[1.5rem] border border-primary-red/5 mb-6 w-full max-h-[350px] overflow-y-auto scrollbar-thin scrollbar-thumb-primary-red/20">
                            <p className="text-base font-poppins text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-line">
                                {t('surprise.revealedMessage')}
                            </p>
                        </div>
                        <div className="flex space-x-3">
                            {[1, 2, 3].map(i => <div key={i} className="text-xl animate-bounce" style={{ animationDelay: `${i * 0.2}s` }}>✨</div>)}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Surprise;
